import { useEffect } from "react";
import {
  Sparkles, Layout, Globe, Palette, Presentation, BookOpen,
  RectangleHorizontal, Table2, Image, Film, Plus, Minus,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  COLLATERAL_RETAINER_TIERS,
  COLLATERAL_EMPTY_STATE,
} from "../../pages/calculators/collateralPricing";

/**
 * CollateralBlock — embeddable Collateral Suite UI for the unified Quote Builder.
 *
 * Props:
 *   value: state shape from collateralPricing.COLLATERAL_EMPTY_STATE
 *   onChange(next): merges next into value
 *   prices: { landing_page, full_website, ... } — optional override
 *   subtotal: number — for display in header
 */
export default function CollateralBlock({ value, onChange, prices, subtotal = 0 }) {
  // Ensure shape on first mount
  useEffect(() => {
    if (!value || !value.toggles) onChange?.(COLLATERAL_EMPTY_STATE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggles = value?.toggles || COLLATERAL_EMPTY_STATE.toggles;
  const billboardOption = value?.billboardOption || "none";
  const aiRetainerTier = value?.aiRetainerTier || "none";
  const aiEnhanceQty = value?.aiEnhanceQty ?? 3;
  const retainerMonths = value?.retainerMonths ?? 6;
  const p = prices || {};

  const setToggle = (id, val, excludes = null) => {
    const next = { ...toggles, [id]: val };
    if (val && excludes) next[excludes] = false;
    onChange?.({ ...value, toggles: next });
  };
  const setField = (k, v) => onChange?.({ ...value, [k]: v });

  return (
    <Card className="overflow-hidden" data-section="collateral-suite">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            Collateral Suite
          </CardTitle>
          {subtotal > 0 && (
            <span className="text-lg font-semibold text-amber-500 tabular-nums" data-testid="collateral-subtotal">
              ${subtotal.toLocaleString()}
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Branding, digital presence, sales collateral, and AI-enhanced marketing content.
        </p>
      </CardHeader>

      <CardContent className="space-y-6 pt-0">
        {/* Digital Presence */}
        <Group title="Digital Presence">
          <ToggleRow
            icon={Layout} label="Landing Page" price={p.landing_page}
            sub="Single high-converting page for lead capture and campaign traffic."
            checked={!!toggles.landing_page}
            onChange={(v) => setToggle("landing_page", v, "full_website")}
            testId="collateral-landing-page"
          />
          <ToggleRow
            icon={Globe} label="Full Website" price={p.full_website}
            sub="Multi-page website with full structure and user journey."
            checked={!!toggles.full_website}
            onChange={(v) => setToggle("full_website", v, "landing_page")}
            testId="collateral-full-website"
          />
        </Group>

        {/* Branding & Positioning */}
        <Group title="Branding & Positioning">
          <ToggleRow
            icon={Palette} label="Branding & Conceptual Package" price={p.branding}
            sub="A complete identity system — positioning, name and messaging, logo, brand deck, one sheet, and location map — delivered as production-ready files."
            checked={!!toggles.branding}
            onChange={(v) => setToggle("branding", v)}
            testId="collateral-branding"
          />
          <ToggleRow
            icon={Presentation} label="Investment Presentation" price={p.investment_presentation}
            sub="Investor-facing deck (up to 20 pages)."
            checked={!!toggles.investment_presentation}
            onChange={(v) => setToggle("investment_presentation", v)}
            testId="collateral-investment-presentation"
          />
        </Group>

        {/* Sales & Marketing Collateral */}
        <Group title="Sales & Marketing Collateral">
          <ToggleRow
            icon={BookOpen} label="Development Flipbook" price={p.flipbook}
            sub="Comprehensive sales/marketing book (40–50 pages)."
            checked={!!toggles.flipbook}
            onChange={(v) => setToggle("flipbook", v)}
            testId="collateral-flipbook"
          />
          {/* Billboard dropdown */}
          <div className="flex items-start gap-3 px-1 py-2">
            <RectangleHorizontal className="w-4 h-4 text-muted-foreground mt-1 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3">
                <Label className="text-sm font-medium">Billboard Design</Label>
                <Select value={billboardOption} onValueChange={(v) => setField("billboardOption", v)}>
                  <SelectTrigger className="w-[210px] h-8" data-testid="collateral-billboard"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="static">Static — ${p.billboard_static?.toLocaleString()}</SelectItem>
                    <SelectItem value="animated">Animated (≤10s) — ${p.billboard_animated?.toLocaleString()}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">Billboard-ready creative using project visuals.</p>
            </div>
          </div>
          <ToggleRow
            icon={Table2} label="Unit Inventory Breakdown Sheet" price={p.unit_inventory}
            sub="Clean, structured unit matrix for sales teams."
            checked={!!toggles.unit_inventory}
            onChange={(v) => setToggle("unit_inventory", v)}
            testId="collateral-unit-inventory"
          />
        </Group>

        {/* AI-Enhanced Marketing Content */}
        <Group title="AI-Enhanced Marketing Content">
          {/* AI Social retainer */}
          <div className="flex items-start gap-3 px-1 py-2">
            <Film className="w-4 h-4 text-muted-foreground mt-1 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3">
                <Label className="text-sm font-medium">AI Social Media Content</Label>
                <Select value={aiRetainerTier} onValueChange={(v) => setField("aiRetainerTier", v)}>
                  <SelectTrigger className="w-[180px] h-8" data-testid="collateral-ai-retainer-tier"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COLLATERAL_RETAINER_TIERS.map(t => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.value === "none"
                          ? "None"
                          : `${t.label} — $${(p[t.monthlyKey] || 0).toLocaleString()}/mo`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                15-second clips from existing renders for ads & social.
              </p>
              {aiRetainerTier !== "none" && (
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-xs text-muted-foreground">Retainer length</span>
                  <NumStepper
                    value={retainerMonths}
                    onChange={(v) => setField("retainerMonths", Math.max(1, v))}
                    min={1} max={24}
                    suffix="months"
                    testId="collateral-retainer-months"
                  />
                </div>
              )}
            </div>
          </div>

          {/* AI Enhance */}
          <div className="flex items-start gap-3 px-1 py-2">
            <Image className="w-4 h-4 text-muted-foreground mt-1 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3">
                <Label className="text-sm font-medium">Advanced Render Enhancement</Label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground tabular-nums">${p.ai_enhance?.toLocaleString()}/img</span>
                  <Switch
                    checked={!!toggles.ai_enhance}
                    onCheckedChange={(v) => setToggle("ai_enhance", v)}
                    data-testid="collateral-ai-enhance-toggle"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                AI upscaling + Photoshop polish + cinematic post-production.
              </p>
              {toggles.ai_enhance && (
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-xs text-muted-foreground">Quantity</span>
                  <NumStepper
                    value={aiEnhanceQty}
                    onChange={(v) => setField("aiEnhanceQty", Math.max(1, v))}
                    min={1} max={500}
                    suffix="images"
                    testId="collateral-ai-enhance-qty"
                  />
                </div>
              )}
            </div>
          </div>
        </Group>
      </CardContent>
    </Card>
  );
}

function Group({ title, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground mb-2 px-1">{title}</div>
      <div className="rounded-lg border border-border/60 divide-y divide-border/40">
        {children}
      </div>
    </div>
  );
}

function ToggleRow({ icon: Icon, label, price, sub, checked, onChange, testId }) {
  return (
    <div className="flex items-start gap-3 px-3 py-2.5">
      <Icon className="w-4 h-4 text-muted-foreground mt-1 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-3">
          <Label className="text-sm font-medium">{label}</Label>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground tabular-nums">${(price || 0).toLocaleString()}</span>
            <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
          </div>
        </div>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function NumStepper({ value, onChange, min = 0, max = 999, suffix, testId }) {
  return (
    <div className="inline-flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        className="w-6 h-6 rounded-md bg-muted hover:bg-muted-foreground/20 flex items-center justify-center"
        data-testid={`${testId}-dec`}
      >
        <Minus className="w-3 h-3" />
      </button>
      <span className="text-sm font-medium tabular-nums min-w-[1.5rem] text-center">{value}</span>
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        className="w-6 h-6 rounded-md bg-muted hover:bg-muted-foreground/20 flex items-center justify-center"
        data-testid={`${testId}-inc`}
      >
        <Plus className="w-3 h-3" />
      </button>
      {suffix && <span className="text-xs text-muted-foreground ml-1">{suffix}</span>}
    </div>
  );
}
